import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { memo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';

import { StatusStamp } from '../../components/StatusStamp';
import { useJobItemVersions } from '../api/use-job-item-versions';

type DatedField = 'reviewedAt' | 'publishedAt';
type VersionWithDate<F extends DatedField> = JobProfileVersion & {
    [K in F]: string;
};

const findHighest = <F extends DatedField>(
    versions: readonly JobProfileVersion[],
    field: F,
): VersionWithDate<F> | undefined => {
    return versions
        .filter((v): v is VersionWithDate<F> => !is.nullOrUndefined(v[field]))
        .toSorted((a, b) => b.version - a.version)
        .at(0);
};

type JobProfileStatusStampsProps = {
    readonly architectureId: string;
    readonly currentVersion: number;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
};

export const JobProfileStatusStamps: FunctionComponent<JobProfileStatusStampsProps> = memo(
    function JobProfileStatusStamps({ architectureId, currentVersion, itemId, itemType }) {
        const { applicationState } = useApplicationContext();
        const { data: versions } = useJobItemVersions({
            architectureId,
            itemId,
            itemType,
        });

        if (is.nullOrUndefined(versions) || is.emptyArray(versions)) {
            return null;
        }

        const highestReviewed = findHighest(versions, 'reviewedAt');
        const highestPublished = findHighest(versions, 'publishedAt');

        if (is.nullOrUndefined(highestReviewed) && is.nullOrUndefined(highestPublished)) {
            return null;
        }

        const localeCode = applicationState.language.code;

        return (
            <div className="flex shrink-0 items-center gap-[10px]" data-testid="job-profile-status-stamps">
                {!is.nullOrUndefined(highestReviewed) ? (
                    <StatusStamp
                        date={highestReviewed.reviewedAt}
                        kind="reviewed"
                        localeCode={localeCode}
                        muted={highestReviewed.version !== currentVersion}
                        version={highestReviewed.version}
                    />
                ) : null}
                {!is.nullOrUndefined(highestPublished) ? (
                    <StatusStamp
                        date={highestPublished.publishedAt}
                        kind="published"
                        localeCode={localeCode}
                        muted={highestPublished.version !== currentVersion}
                        version={highestPublished.version}
                    />
                ) : null}
            </div>
        );
    },
);
